import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlaskConical, Plus, X, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import {
  useProtocols,
  useCurrentProtocol,
  useCreateProtocol,
} from '@/api/hooks/useProtocol';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

interface CompoundEntry {
  name: string;
  dose: string;
  frequency: string;
  duration: string;
  source: string;
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'secondary'> = {
  natural: 'success',
  trt: 'default',
  peptides: 'warning',
  glp1: 'warning',
  mixed: 'secondary',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const emptyCompound = (): CompoundEntry => ({
  name: '',
  dose: '',
  frequency: '',
  duration: '',
  source: '',
});

export function ProtocolPage() {
  const { data: currentData, isLoading: loadingCurrent } = useCurrentProtocol();
  const { data: allData, isLoading: loadingAll } = useProtocols();
  const createProtocol = useCreateProtocol();

  const [formOpen, setFormOpen] = useState(false);
  const [status, setStatus] = useState('natural');
  const [compounds, setCompounds] = useState<CompoundEntry[]>([emptyCompound()]);
  const [startDate, setStartDate] = useState('');
  const [notes, setNotes] = useState('');

  const addCompound = () => setCompounds((c) => [...c, emptyCompound()]);
  const removeCompound = (i: number) =>
    setCompounds((c) => c.filter((_, idx) => idx !== i));
  const updateCompound = (i: number, field: keyof CompoundEntry, value: string) =>
    setCompounds((c) =>
      c.map((item, idx) => (idx === i ? { ...item, [field]: value } : item))
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const items = compounds
      .filter((c) => c.name.trim())
      .map((c) => ({
        type: 'compound',
        name: c.name,
        dosage: c.dose,
        frequency: c.frequency,
        notes: [c.duration, c.source].filter(Boolean).join(' | '),
      }));

    try {
      await createProtocol.mutateAsync({
        name: `${status} protocol`,
        items,
      });
      setFormOpen(false);
      setCompounds([emptyCompound()]);
      setStatus('natural');
      setNotes('');
      setStartDate('');
    } catch {
      // error on createProtocol.error
    }
  };

  const currentProtocol = currentData?.protocol;
  const allProtocols = allData?.protocols ?? [];
  const isLoading = loadingCurrent || loadingAll;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy">Protocol</h1>
        <p className="mt-1 text-gray-500">
          Track your supplements, TRT, peptides, or other protocols to improve analysis accuracy
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-copper border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Current Protocol */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-copper" />
                Current Protocol
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentProtocol ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Badge
                      variant={STATUS_COLORS[currentProtocol.status] ?? 'secondary'}
                      className="capitalize"
                    >
                      {currentProtocol.status}
                    </Badge>
                    <span className="text-xs text-gray-400">
                      Updated {formatDate(currentProtocol.updated_at)}
                    </span>
                  </div>

                  {currentProtocol.items?.length > 0 ? (
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Compound
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Dose
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Frequency
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {currentProtocol.items.map((item, i) => (
                            <tr key={i}>
                              <td className="px-4 py-2.5 font-medium text-navy">
                                {item.name}
                              </td>
                              <td className="px-4 py-2.5 text-gray-600">
                                {item.dosage || '-'}
                              </td>
                              <td className="px-4 py-2.5 text-gray-600">
                                {item.frequency || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No compounds listed in this protocol.
                    </p>
                  )}

                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setFormOpen(true)}
                  >
                    Update Protocol
                  </Button>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <FlaskConical className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No protocol set</p>
                  <p className="text-sm text-gray-400 mt-1 mb-4">
                    Add your current protocol so we can provide more relevant analysis
                  </p>
                  <Button onClick={() => setFormOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Protocol
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add/Update Protocol Form */}
          <AnimatePresence>
            {formOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>
                        {currentProtocol ? 'Update Protocol' : 'New Protocol'}
                      </CardTitle>
                      <button
                        onClick={() => setFormOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <ChevronUp className="h-5 w-5" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Protocol Type</Label>
                          <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="natural">Natural (no PEDs)</SelectItem>
                              <SelectItem value="trt">TRT (Testosterone Replacement)</SelectItem>
                              <SelectItem value="peptides">Peptides</SelectItem>
                              <SelectItem value="glp1">GLP-1 (Semaglutide/Tirzepatide)</SelectItem>
                              <SelectItem value="mixed">Mixed / Multiple</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="start-date">Start Date</Label>
                          <Input
                            id="start-date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Compounds */}
                      {status !== 'natural' && (
                        <div className="space-y-3">
                          <Label>Compounds</Label>
                          {compounds.map((compound, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="grid grid-cols-12 gap-2 items-start rounded-lg bg-gray-50 p-3"
                            >
                              <div className="col-span-12 sm:col-span-3">
                                <Input
                                  placeholder="Compound name"
                                  value={compound.name}
                                  onChange={(e) =>
                                    updateCompound(i, 'name', e.target.value)
                                  }
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-2">
                                <Input
                                  placeholder="e.g., 200mg"
                                  value={compound.dose}
                                  onChange={(e) =>
                                    updateCompound(i, 'dose', e.target.value)
                                  }
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-2">
                                <Input
                                  placeholder="e.g., weekly"
                                  value={compound.frequency}
                                  onChange={(e) =>
                                    updateCompound(i, 'frequency', e.target.value)
                                  }
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-2">
                                <Input
                                  placeholder="e.g., 6 months"
                                  value={compound.duration}
                                  onChange={(e) =>
                                    updateCompound(i, 'duration', e.target.value)
                                  }
                                />
                              </div>
                              <div className="col-span-5 sm:col-span-2">
                                <Select
                                  value={compound.source}
                                  onValueChange={(v) => updateCompound(i, 'source', v)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Source" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="prescribed">Prescribed</SelectItem>
                                    <SelectItem value="self_administered">
                                      Self Administered
                                    </SelectItem>
                                    <SelectItem value="research_chemical">
                                      Research Chemical
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-1 flex items-center justify-center pt-1">
                                {compounds.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeCompound(i)}
                                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </motion.div>
                          ))}

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addCompound}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Compound
                          </Button>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="protocol-notes">Notes</Label>
                        <Textarea
                          id="protocol-notes"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Any additional notes about your protocol"
                          rows={3}
                        />
                      </div>

                      {createProtocol.error && (
                        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                          {createProtocol.error.message}
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button type="submit" disabled={createProtocol.isPending}>
                          {createProtocol.isPending
                            ? 'Saving...'
                            : 'Save Protocol'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setFormOpen(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Protocol History */}
          {allProtocols.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-gray-400" />
                  Protocol History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />

                  <div className="space-y-6">
                    {allProtocols.map((protocol) => (
                      <div key={protocol.id} className="relative pl-8">
                        {/* Timeline dot */}
                        <div
                          className={`absolute left-1.5 top-1 h-3 w-3 rounded-full border-2 border-white ${
                            protocol.status === 'active'
                              ? 'bg-copper'
                              : 'bg-gray-300'
                          }`}
                        />

                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-navy capitalize">
                              {protocol.name}
                            </span>
                            <Badge
                              variant={
                                protocol.status === 'active'
                                  ? 'success'
                                  : 'secondary'
                              }
                              className="capitalize text-[10px]"
                            >
                              {protocol.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-400">
                            {formatDate(protocol.created_at)}
                          </p>
                          {protocol.items?.length > 0 && (
                            <ul className="mt-2 text-xs text-gray-500 space-y-0.5">
                              {protocol.items.map((item, j) => (
                                <li key={j}>
                                  {item.name}
                                  {item.dosage ? ` - ${item.dosage}` : ''}
                                  {item.frequency ? ` (${item.frequency})` : ''}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
